import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
  condenseTaxInformation,
  TaxInformation,
  taxMapper,
} from '../../util/taxCalculatorHelpers';
import {
  applyServerLinePrices,
  ServerLinePrice,
} from '../../util/bookingReprice';

export interface ServiceType {
  id: number;
  name: string;
  nameKey?: string | null;
  value: number;
  isEstimate: boolean,
  description: string;
  subDescription: string | null;
  sortOrder: number | null;
  serviceTypeId: number,
  serviceTypeName: string,
  taxName: string;
  taxPercent: number;
  taxId: number;
  discountedValue?: number
  active?: boolean;
  /**
   * Why this price is not simply the admin-set number, when a tenant pricing
   * rule computed it from the vehicle (e.g. "European make: +$60.00").
   */
  priceNote?: string | null;
  /**
   * The same explanation as numbers (base, European/diesel surcharge, extra
   * litres), on an annual plan a tenant pricing rule priced from the vehicle.
   * Mirrors server `PlanPriceBreakdown`; null/absent otherwise.
   */
  planPriceBreakdown?: PlanPriceBreakdown | null;
  /** On the consumer skin's visit list: which consumer service this row is. */
  consumerKind?: string | null;
}

export interface PlanPriceBreakdown {
  basePriceCents: number;
  engineSurchargeCents: number;
  /** 'european', 'diesel' or null. */
  engineSurchargeReason: string | null;
  /** Litres over the allowance, pro rata (e.g. 1.5). */
  extraLitres: number;
  capacitySurchargeCents: number;
  /** Every surcharge together, after rounding. */
  surchargeCents?: number | null;
  /** The litres per oil change the base plan covers (the tenant's setting). */
  includedLitres?: number | null;
  /** What each extra litre costs a year, cents (the tenant's setting). */
  extraLitreRateCents?: number | null;
  /** The step the surcharges were rounded to, cents (0 = none). */
  surchargeRoundToCents?: number | null;
  totalCents: number;
  /**
   * Monthly plan only (null/absent on an annual plan): the premium for paying
   * monthly and the 12-payment split of `totalCents` + that premium.
   */
  monthlyPremiumCents?: number | null;
  instalmentCount?: number | null;
  /** Payments 1..11; the booking charges payment 1. */
  instalmentCents?: number | null;
  /** Payment 12, which takes the rounding remainder. */
  lastInstalmentCents?: number | null;
  termTotalCents?: number | null;
}
export interface ServiceTypesState {
  services: ServiceType[];
}

const initialState: ServiceTypesState = {
  services: [],
};

export function toServiceAnalytics(services: ServiceType[]) {
  const ids = services.map((s) => s.id);
  const names = services.map((s) => s.name);
  const typeIds = services.map((s) => s.serviceTypeId);
  const values = services.map((s) => s.value ?? 0);

  const total = values.reduce((sum, v) => sum + (v || 0), 0);
  const anyDiscount = services.some((s) => s.discountedValue != null);

  return {
    service_ids: ids,
    service_names: names,
    service_type_ids: typeIds,
    service_values: values,
    service_count: services.length,
    services_total_value: +total.toFixed(2),
    services_any_discount: anyDiscount,
  };
}

export const serviceTypesSlice = createSlice({
  name: 'serviceTypes',
  initialState,
  reducers: {
    addService: (state, action: PayloadAction<ServiceType>) => {
      state.services.push(action.payload);
    },
    removeService: (state, action: PayloadAction<number>) => (
      {
        ...state,
        services: state
          .services
          .filter((service) => service.id !== action.payload),
      }
    ),
    clearAllSelectedServices: (state) => (
      {
        ...state,
        services: [],
      }
    ),
    /** The server's prices for the basket (a BOOKING_PRICE_CHANGED refusal). */
    repriceServices: (state, action: PayloadAction<ServerLinePrice[]>) => {
      applyServerLinePrices(state.services, action.payload);
    },
  },
});

export const selectServicesTotal = (
  state: RootState,
) => (state.serviceTypes.services.length
  ? Math.round(state.serviceTypes.services
    .map((service) => (
      service.discountedValue !== null
      && service.discountedValue !== undefined
      && Number.isFinite(service.discountedValue)
        ? service.discountedValue
        : service.value
    ))
    .reduce((prev, current) => prev + current) * 100) / 100
  : 0
);

export const selectServiceTaxTypesAndAmounts = (
  state: RootState,
) => {
  const mappedTaxes: TaxInformation[] = state.serviceTypes.services.length
    ? state.serviceTypes.services.map((item) => taxMapper(item))
    : [];

  return condenseTaxInformation(mappedTaxes);
};

export const {
  addService,
  removeService,
  clearAllSelectedServices,
  repriceServices,
} = serviceTypesSlice.actions;

export const selectServiceTypes = (
  state: RootState,
) => state.serviceTypes.services;

export default serviceTypesSlice.reducer;
